import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Label, FormGroup } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../custom/BackButton';
import { FormProvider, useForm } from 'react-hook-form';
import { createdUpdatedInfo, formatDatetime, formatEmail, getTsDate } from '../../../../utils';
import useUser from '../../../../customHooks/useUser';
import { Button } from 'reactstrap';
import Input from '../../../Input';
import { PASSENGER_STAT_TYPE, PassengerStat, PassengerStatsSchema, PassengerStatsSchemaUpdate } from './validation';
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalFooter } from 'reactstrap';
import ActionIcon from '../../../custom/ActionIcon';
import moment from 'moment';
import SelectAirport from '../../../custom/SelectAirport';
import { AircraftType, DelayCode, IAirport, SelectOption, SelectOptions } from '../../../../globals/interfaces';
import SelectField from '../../../SelectField';
import { ButtonGroup } from '@material-ui/core';
import MilitaryDatePicker from '../../../MilitaryDatePicker';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    record?: PassengerStat;
    onSubmit: (data: PassengerStat | PassengerStat[], update?: boolean) => void;
    validateStat: () => Promise<void>;
    deleteStat: (id: number) => Promise<void>;
    airportCodes: Array<IAirport>;
    airportCodesMap: Record<string, string>;
    aircraftTypeOptions: SelectOptions;
    delayCodeOptions: SelectOptions;
}

export default function PaxStatsModal({
    modal,
    setModal,
    record,
    onSubmit,
    validateStat,
    deleteStat,
    airportCodes,
    airportCodesMap,
    aircraftTypeOptions,
    delayCodeOptions
}: Props) {

    const { user } = useUser();
    const isManager = user.i_access_level >= 4;
    const update = Boolean(record);
    const renderManagerButtons = update && isManager;

    // State for dual form mode
    const [isDualMode, setIsDualMode] = useState(false);

    // Form methods for inbound form
    const inboundMethods = useForm<PassengerStat>({
        resolver: update ? zodResolver(PassengerStatsSchemaUpdate) : zodResolver(PassengerStatsSchema),
        mode: "onChange",
        reValidateMode: "onChange",
        shouldFocusError: true,
        context: { valueAsNumber: true },
    });

    // Form methods for outbound form (only used in dual mode)
    const outboundMethods = useForm<PassengerStat>({
        resolver: zodResolver(PassengerStatsSchema),
        mode: "onChange",
        reValidateMode: "onChange",
        shouldFocusError: true,
        context: { valueAsNumber: true },
    });

    const {
        handleSubmit: handleInboundSubmit,
        reset: resetInbound,
        clearErrors: clearInboundErrors,
        trigger: triggerInbound,
        formState: { errors: inboundErrors, isValid: inboundIsValid, isDirty: inboundIsDirty },
        watch: watchInbound,
        setValue: setInboundValue
    } = inboundMethods;

    const {
        handleSubmit: handleOutboundSubmit,
        reset: resetOutbound,
        clearErrors: clearOutboundErrors,
        trigger: triggerOutbound,
        formState: { errors: outboundErrors, isValid: outboundIsValid, isDirty: outboundIsDirty },
        watch: watchOutbound,
        setValue: setOutboundValue
    } = outboundMethods;

    // Watch values for inbound form
    const inboundOrigin = watchInbound('origin');
    const inboundDestination = watchInbound('destination');
    const inboundPlannedTime = watchInbound('planned_time');
    const inboundActualTime = watchInbound('actual_time');
    const inboundType = watchInbound('type');

    // Watch values for outbound form
    const outboundOrigin = watchOutbound('origin');
    const outboundDestination = watchOutbound('destination');
    const outboundPlannedTime = watchOutbound('planned_time');
    const outboundActualTime = watchOutbound('actual_time');
    const outboundType = watchOutbound('type');

    // Reset forms when modal opens/closes or record changes
    useEffect(() => {
        if (modal) {
            clearInboundErrors();
            clearOutboundErrors();
            const now = getTsDate();

            if (record) {
                // In update mode, only populate inbound form and disable dual mode
                setIsDualMode(false);
                resetInbound({
                    ...record,
                    planned_time: moment.utc(record.planned_time).format('YYYY-MM-DDTHH:mm'),
                    actual_time: moment.utc(record.actual_time).format('YYYY-MM-DDTHH:mm'),
                    modified: now,
                    modified_by: user.s_email,
                    unit: record.unit || user.s_unit
                });
            } else {
                // Create mode - set up both forms
                const defaultInboundData = {
                    status: "PENDING" as const,
                    type: "INBOUND" as const,
                    date: moment().format('YYYY-MM-DD'),
                    number: "",
                    registration: "",
                    model: "",
                    origin: "",
                    destination: "",
                    planned_time: "",
                    actual_time: "",
                    delay_mins: undefined,
                    delay_codes: "",
                    bill_mins: undefined,
                    created: now,
                    created_by: user.s_email,
                    modified: now,
                    modified_by: user.s_email,
                    validated: "",
                    validated_by: "",
                    notes: "",
                    unit: user.s_unit
                };

                const defaultOutboundData = {
                    ...defaultInboundData,
                    type: "OUTBOUND" as const,
                };

                resetInbound(defaultInboundData);
                resetOutbound(defaultOutboundData);
            }

            triggerInbound();
            if (!update) {
                triggerOutbound();
            }
        }
    }, [modal, record, resetInbound, resetOutbound, clearInboundErrors, clearOutboundErrors, user.s_email, update]);

    // Calculate delay minutes for inbound
    useEffect(() => {
        if (inboundPlannedTime && inboundActualTime) {
            setInboundValue('delay_mins', Number(moment(inboundActualTime).diff(moment(inboundPlannedTime), 'minute')), {
                shouldValidate: true,
                shouldDirty: true
            });
        }
    }, [inboundPlannedTime, inboundActualTime, setInboundValue]);

    // Calculate delay minutes for outbound
    useEffect(() => {
        if (outboundPlannedTime && outboundActualTime) {
            setOutboundValue('delay_mins', Number(moment(outboundActualTime).diff(moment(outboundPlannedTime), 'minute')), {
                shouldValidate: true,
                shouldDirty: true
            });
        }
    }, [outboundPlannedTime, outboundActualTime, setOutboundValue]);

    const handleSetAirportCode = (formType: 'inbound' | 'outbound', field: 'origin' | 'destination', value: string) => {
        if (formType === 'inbound') {
            setInboundValue(field, value, {
                shouldTouch: true,
                shouldValidate: true,
                shouldDirty: true
            });
        } else {
            setOutboundValue(field, value, {
                shouldTouch: true,
                shouldValidate: true,
                shouldDirty: true
            });
        }
    };

    const handleFormSubmit = () => {
        if (isDualMode && !update) {
            // Submit both forms
            Promise.all([
                new Promise<PassengerStat>((resolve) => {
                    handleInboundSubmit(resolve)();
                }),
                new Promise<PassengerStat>((resolve) => {
                    handleOutboundSubmit(resolve)();
                })
            ]).then(([inboundData, outboundData]) => {
                onSubmit([inboundData, outboundData], false);
            });
        } else {
            // Submit single form
            handleInboundSubmit(data => onSubmit(data, update))();
        }
    };

    const isFormValid = isDualMode && !update ?
        (inboundIsValid && outboundIsValid) :
        inboundIsValid;

    const isFormDirty = isDualMode && !update ?
        (inboundIsDirty || outboundIsDirty) :
        inboundIsDirty;

    const toggle = () => setModal(!modal);

    const handleDualMode = (isDualMode) => {
        if (isDualMode) {
            setInboundValue('type', 'INBOUND');
            setOutboundValue('type', 'OUTBOUND');
        }
        setIsDualMode(isDualMode);
    }

    const renderFormSection = (
        methods: typeof inboundMethods,
        formType: 'inbound' | 'outbound',
        origin: string,
        destination: string,
        type: string,
        setValue: typeof setInboundValue
    ) => {
        const airportArray: Array<{ field: 'origin' | 'destination', value: string, label: string }> = [{
            field: 'origin',
            value: origin,
            label: 'Origin'
        }, {
            field: 'destination',
            value: destination,
            label: 'Destination'
        }];

        return (
            <FormProvider {...methods}>
                <FormSectionContainer>
                    {isDualMode && (
                        <SectionHeader>
                            <h5>{formType === 'inbound' ? 'Inbound Flight' : 'Outbound Flight'}</h5>
                        </SectionHeader>
                    )}
                    <FlexContainer isDual={isDualMode}>
                        <div>
                            <h6>Flight Information</h6>
                            <FormGroup>
                                <Label>Type</Label>
                                <ButtonGroup className={isDualMode ? 'custom-disabled' : ''}>
                                    {PASSENGER_STAT_TYPE.map((item, i) => (
                                        <Button
                                            key={i}
                                            active={item === type}
                                            onClick={() => setValue('type', item, { shouldValidate: true, shouldDirty: true })}
                                            color={item === type ? 'green' : 'grey'}
                                        >
                                            {item}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </FormGroup>
                            <Input label="Number" name="number" required />
                            <Input label="Registration" name="registration" required />
                            <SelectField
                                name="model"
                                label="Model"
                                options={aircraftTypeOptions}
                            />
                        </div>
                        <div>
                            <h6>Itinerary</h6>
                            <FlexContainer>
                                {
                                    airportArray.map((item, i) => (
                                        <SelectAirport
                                            airports={airportCodes}
                                            airportCodesMap={airportCodesMap}
                                            code={item.value}
                                            setCode={code => handleSetAirportCode(formType, item.field, code)}
                                            label={item.label}
                                            block
                                            key={i}
                                            tooltip='Airport code'
                                        />
                                    ))
                                }
                            </FlexContainer>
                            <FormGroup>
                                <MilitaryDatePicker
                                    label={'Planned Time'}
                                    initialValue={formType === 'inbound' ? inboundPlannedTime : outboundPlannedTime}
                                    onChange={(val) => setValue('planned_time', val, { shouldValidate: true, shouldDirty: true })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <MilitaryDatePicker
                                    label={'Actual Time'}
                                    initialValue={formType === 'inbound' ? inboundActualTime : outboundActualTime}
                                    onChange={(val) => setValue('actual_time', val, { shouldValidate: true, shouldDirty: true })}
                                />
                            </FormGroup>
                        </div>
                        <div>
                            <h6>Delays</h6>
                            <Input label="Delay Mins" type="number" name="delay_mins" required />
                            <SelectField
                                name="delay_codes"
                                label="Delay Codes"
                                options={delayCodeOptions}
                                isMultiple
                            />
                            <Input label="Billable Minutes" type="number" name="bill_mins" required />
                        </div>
                    </FlexContainer>
                    <Input label="Notes" name="notes" />
                </FormSectionContainer>
            </FormProvider>
        );
    };

    return (
        <CustomModal isOpen={modal} isDual={isDualMode}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <div>
                        <BackButton onClick={toggle} />
                        <h4 className={'pl-2'}>Flight Stats</h4>
                    </div>
                    <HeaderButtonsContainer>
                        {!update && (
                            <Button
                                color={isDualMode ? "primary" : "outline-primary"}
                                className="mr-2"
                                onClick={() => handleDualMode(!isDualMode)}
                            >
                                {isDualMode ? "Half Turn" : "Full Turn"}
                            </Button>
                        )}
                        {user.i_access_level >= 4 && (
                            <Button color='warning'>Manager Access</Button>
                        )}
                    </HeaderButtonsContainer>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FormsContainer isDual={isDualMode}>
                    {renderFormSection(
                        inboundMethods,
                        'inbound',
                        inboundOrigin,
                        inboundDestination,
                        inboundType,
                        setInboundValue
                    )}

                    {isDualMode && !update && renderFormSection(
                        outboundMethods,
                        'outbound',
                        outboundOrigin,
                        outboundDestination,
                        outboundType,
                        setOutboundValue
                    )}
                </FormsContainer>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label className={'d-block'}>
                        {createdUpdatedInfo(record, true)}
                    </Label>
                    {
                        record?.status === 'VALIDATED' && (
                            <Label>Validated by {formatEmail(record.validated_by)} on {formatDatetime(record.validated)}</Label>
                        )
                    }
                    <FooterButtonsContainer update={update}>
                        {
                            renderManagerButtons && (
                                <ActionIcon
                                    type={'delete'}
                                    onClick={() => deleteStat(record.id)}
                                />
                            )
                        }
                        <FooterFlex>
                            {
                                renderManagerButtons && (
                                    <Button
                                        color="secondary"
                                        className="mr-2"
                                        onClick={validateStat}
                                    >
                                        Validate
                                    </Button>
                                )
                            }
                            <ActionIcon
                                type={'save'}
                                disabled={!isFormDirty || !isFormValid}
                                onClick={handleFormSubmit}
                                simulateLoading
                            />
                        </FooterFlex>
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </CustomModal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    & div {
        display: flex;
        align-items: center;
    }
`;

const HeaderButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const FormsContainer = styled.div<{ isDual: boolean }>`
    display: flex;
    flex-direction: ${props => props.isDual ? 'row' : 'column'};
    gap: ${props => props.isDual ? '30px' : '0'};
    width: 100%;
`;

const FormSectionContainer = styled.div`
    flex: 1;
    min-width: 0;
`;

const SectionHeader = styled.div`
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #e9ecef;
    
    h5 {
        margin: 0;
        color: #495057;
        font-weight: 600;
    }
`;

const FlexContainer = styled.div<{ isDual?: boolean }>`
    display: flex;
    flex-direction: ${props => props.isDual ? 'column' : 'row'};
    gap: ${props => props.isDual ? '15px' : '25px'};
    width: 100%;

    & > div {
        width: 100%;
    }
`;

const FooterFlex = styled.div`
    display: flex;
`;

const CustomModal = styled(Modal) <{ isDual?: boolean }>`
    width: 100%;
    max-width: ${props => props.isDual ? '1400px' : '1000px'};
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;